import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { breakpoints } from "@obs/tokens/index.stylex.ts";
import type { StyleXStyles } from "@stylexjs/stylex";
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";

type Breakpoint = "sm" | "md" | "lg" | "xl";

type ResponsiveValue<T> = T | Partial<Record<Breakpoint, T>>;

type MaxWidth = "sm" | "md" | "lg" | "xl" | "full" | number;

export type ContainerProps = {
	children: ReactNode;
	render?: useRender.ComponentProps<"div">["render"];
	maxWidth?: ResponsiveValue<MaxWidth>;
	centered?: boolean;
	sx?: StyleXStyles;
};

const styles = stylex.create({
	root: {
		display: "flex",
		flexDirection: "column",
		inlineSize: "100%",
	},
	centered: {
		marginInline: "auto",
	},
	// Static maxWidth variants
	maxWidthSm: { maxInlineSize: "640px" },
	maxWidthMd: { maxInlineSize: "800px" },
	maxWidthLg: { maxInlineSize: "960px" },
	maxWidthXl: { maxInlineSize: "1272px" },
	maxWidthFull: { maxInlineSize: "100%" },
	// Responsive maxWidth at sm breakpoint
	smMaxWidthSm: { maxInlineSize: { default: null, [breakpoints.sm]: "640px" } },
	smMaxWidthMd: { maxInlineSize: { default: null, [breakpoints.sm]: "800px" } },
	smMaxWidthLg: { maxInlineSize: { default: null, [breakpoints.sm]: "960px" } },
	smMaxWidthXl: { maxInlineSize: { default: null, [breakpoints.sm]: "1272px" } },
	smMaxWidthFull: { maxInlineSize: { default: null, [breakpoints.sm]: "100%" } },
	// Responsive maxWidth at md breakpoint
	mdMaxWidthSm: { maxInlineSize: { default: null, [breakpoints.md]: "640px" } },
	mdMaxWidthMd: { maxInlineSize: { default: null, [breakpoints.md]: "800px" } },
	mdMaxWidthLg: { maxInlineSize: { default: null, [breakpoints.md]: "960px" } },
	mdMaxWidthXl: { maxInlineSize: { default: null, [breakpoints.md]: "1272px" } },
	mdMaxWidthFull: { maxInlineSize: { default: null, [breakpoints.md]: "100%" } },
	// Responsive maxWidth at lg breakpoint
	lgMaxWidthSm: { maxInlineSize: { default: null, [breakpoints.lg]: "640px" } },
	lgMaxWidthMd: { maxInlineSize: { default: null, [breakpoints.lg]: "800px" } },
	lgMaxWidthLg: { maxInlineSize: { default: null, [breakpoints.lg]: "960px" } },
	lgMaxWidthXl: { maxInlineSize: { default: null, [breakpoints.lg]: "1272px" } },
	lgMaxWidthFull: { maxInlineSize: { default: null, [breakpoints.lg]: "100%" } },
	// Responsive maxWidth at xl breakpoint
	xlMaxWidthSm: { maxInlineSize: { default: null, [breakpoints.xl]: "640px" } },
	xlMaxWidthMd: { maxInlineSize: { default: null, [breakpoints.xl]: "800px" } },
	xlMaxWidthLg: { maxInlineSize: { default: null, [breakpoints.xl]: "960px" } },
	xlMaxWidthXl: { maxInlineSize: { default: null, [breakpoints.xl]: "1272px" } },
	xlMaxWidthFull: { maxInlineSize: { default: null, [breakpoints.xl]: "100%" } },
});

const staticMaxWidthMap: Record<string, stylex.StyleXStyles> = {
	sm: styles.maxWidthSm,
	md: styles.maxWidthMd,
	lg: styles.maxWidthLg,
	xl: styles.maxWidthXl,
	full: styles.maxWidthFull,
};

const responsiveMaxWidthMap: Record<Breakpoint, Record<string, stylex.StyleXStyles>> = {
	sm: {
		sm: styles.smMaxWidthSm,
		md: styles.smMaxWidthMd,
		lg: styles.smMaxWidthLg,
		xl: styles.smMaxWidthXl,
		full: styles.smMaxWidthFull,
	},
	md: {
		sm: styles.mdMaxWidthSm,
		md: styles.mdMaxWidthMd,
		lg: styles.mdMaxWidthLg,
		xl: styles.mdMaxWidthXl,
		full: styles.mdMaxWidthFull,
	},
	lg: {
		sm: styles.lgMaxWidthSm,
		md: styles.lgMaxWidthMd,
		lg: styles.lgMaxWidthLg,
		xl: styles.lgMaxWidthXl,
		full: styles.lgMaxWidthFull,
	},
	xl: {
		sm: styles.xlMaxWidthSm,
		md: styles.xlMaxWidthMd,
		lg: styles.xlMaxWidthLg,
		xl: styles.xlMaxWidthXl,
		full: styles.xlMaxWidthFull,
	},
};

const dynamicStyles = stylex.create({
	maxWidth: (value: string | number) => ({
		maxInlineSize: value,
	}),
});

const getMaxWidthStyles = (maxWidth: ResponsiveValue<MaxWidth>): stylex.StyleXStyles[] => {
	if (typeof maxWidth === "number") {
		return [dynamicStyles.maxWidth(maxWidth)];
	}

	if (typeof maxWidth === "string") {
		return [staticMaxWidthMap[maxWidth]];
	}

	// Responsive object: { sm: "md", lg: "xl" }
	const result: stylex.StyleXStyles[] = [];
	for (const [bp, value] of Object.entries(maxWidth)) {
		if (typeof value === "number") {
			result.push(dynamicStyles.maxWidth(value));
		} else if (value !== undefined) {
			result.push(responsiveMaxWidthMap[bp as Breakpoint][value]);
		}
	}
	return result;
};

export const Container = ({ children, render, maxWidth = "full", centered = true, sx }: ContainerProps) => {
	return useRender({
		render: render ?? <div />,
		props: mergeProps<"div">(
			{ children },
			stylex.props(styles.root, centered && styles.centered, ...getMaxWidthStyles(maxWidth), sx),
		),
	});
};
